import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../api';

interface SkillCall {
  id: string;
  at: string;
  skillCode: string;
  operation: 'query' | 'create' | 'update' | 'delete' | 'list_files' | 'read_file' | 'upload_file';
  object: string;
  fields: string[];
  piiFields: string[];
  recordIds: string[];
  rowCount: number;
  outcome: 'ok' | 'denied' | 'error';
  denialCode: string | null;
  redactions: number;
  redactionPatterns: string[];
  fileIds: string[];
  /** Bytes that went past, either way. Zero on every call that moved no file. */
  fileBytes: number;
  actor: string;
  org: string;
  connectedAs: string;
}

interface Page {
  calls: SkillCall[];
  more: boolean;
  scope: string;
}

const VERB: Record<SkillCall['operation'], string> = {
  query: 'read',
  create: 'created',
  update: 'changed',
  delete: 'deleted',
  list_files: 'listed the files on',
  read_file: 'opened a file on',
  upload_file: 'attached a file to',
};

/** Bytes, for a log line rather than a spreadsheet. */
const size = (bytes: number): string =>
  bytes >= 1_048_576 ? `${(bytes / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/**
 * What agents were actually shown.
 *
 * The session log next door answers "who was in this org, and when". This answers the question that one
 * cannot: which records, which fields, and whether anything was redacted on the way past. It is the thing
 * a compliance team asks for and the reason the brokered path is worth choosing over a direct session, so
 * it is deliberately a page rather than something reconstructed from a database when somebody asks.
 *
 * Record ids, never values. Nothing here ever held the contents of a record and nothing here can show
 * them; following an id means opening Salesforce, where the reader's own permissions apply.
 *
 * Mounted under both the workspace and an org, each of which heads the page with its own Skill log
 * title, so this starts straight at the filters.
 */
export function SkillAudit() {
  // orgId is present only when this page is reached from an org rather than from the workspace, which is
  // what narrows it to that customer.
  const { tenantId, orgId } = useParams<{ tenantId: string; orgId?: string }>();
  const [page, setPage] = useState<Page | null>(null);
  const [redactedOnly, setRedactedOnly] = useState(false);
  const [filesOnly, setFilesOnly] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const params = new URLSearchParams();
      if (redactedOnly) params.set('redactedOnly', 'true');
      if (filesOnly) params.set('filesOnly', 'true');
      if (orgId) params.set('customerOrgId', orgId);
      const query = params.size ? `?${params}` : '';
      setPage(await api<Page>(`/api/tenants/${tenantId}/skill-audit${query}`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the skill log');
    }
  }, [tenantId, orgId, redactedOnly, filesOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      {error && <div className="alert">{error}</div>}

      <div className="card">
        <h3>Show</h3>
        <div className="channel-list" style={{ margin: 0 }}>
          <label className="channel">
            <input type="checkbox" checked={redactedOnly} onChange={() => setRedactedOnly(!redactedOnly)} />
            <span>
              <strong>Only calls where personal data was found</strong>
              <span className="small muted"> — where a pattern matched and something was replaced.</span>
            </span>
          </label>
          <label className="channel">
            <input type="checkbox" checked={filesOnly} onChange={() => setFilesOnly(!filesOnly)} />
            <span>
              <strong>Only calls that read a file</strong>
              <span className="small muted">
                {' '}
                — every call where an agent opened or attached one, with which file and when.
              </span>
            </span>
          </label>
        </div>
        <p className="small muted" style={{ marginBottom: 0 }}>
          One row per call a skill made. Record ids only — no query, no filter and no value a record held has
          ever been written here.{page ? ` Showing ${page.scope}.` : ''}
        </p>
      </div>

      {page && !page.calls.length && (
        <p className="notice" style={{ marginTop: 16 }}>
          {filesOnly
            ? 'No skill has read a file yet.'
            : redactedOnly
            ? 'No call has turned up personal data in a redacted field yet.'
            : 'No skill has been called yet. Rows appear here the moment one is.'}
        </p>
      )}

      {page && page.calls.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {page.calls.map((c) => (
            <div key={c.id} className="listrow" style={{ alignItems: 'flex-start' }}>
              <div style={{ minWidth: 0 }}>
                <div>
                  <strong>{c.actor}</strong> {VERB[c.operation]}{' '}
                  {c.operation === 'query' ? `${c.rowCount} ${c.object}` : `a ${c.object}`}
                  {c.outcome !== 'ok' && <>{' '}<span className="pill off">{c.denialCode ?? c.outcome}</span></>}
                  {c.fileBytes > 0 && (
                    <>{' '}<span className="pill" title="The file was passed through exactly as stored.">
                      {size(c.fileBytes)} file
                    </span></>
                  )}
                  {c.redactions > 0 && (
                    <>{' '}<span className="pill" title={c.redactionPatterns.join(', ')}>
                      {c.redactions} redacted
                    </span></>
                  )}
                </div>
                <div className="small muted">
                  {c.org} · as {c.connectedAs} · skill {c.skillCode} · {new Date(c.at).toLocaleString()}
                </div>
                {expanded === c.id && (
                  <div className="small" style={{ marginTop: 8 }}>
                    <div>
                      <strong>Fields:</strong> {c.fields.length ? c.fields.join(', ') : '—'}
                    </div>
                    {c.piiFields.length > 0 && (
                      <div>
                        <strong>Scanned for personal data:</strong> {c.piiFields.join(', ')}
                        {c.redactions > 0
                          ? ` — ${c.redactions} replaced (${c.redactionPatterns.join(', ')})`
                          : ' — nothing matched'}
                      </div>
                    )}
                    <div>
                      <strong>Records:</strong>{' '}
                      {c.recordIds.length ? <span className="mono">{c.recordIds.join(', ')}</span> : '—'}
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="secondary compact"
                onClick={() => setExpanded(expanded === c.id ? null : c.id)}
              >
                {expanded === c.id ? 'Less' : 'Detail'}
              </button>
            </div>
          ))}
        </div>
      )}

      {page?.more && (
        <p className="small muted" style={{ marginTop: 16 }}>
          There are older calls than these. Redactions and files are the only filters so far; a date range
          and an export are the obvious next ones.
        </p>
      )}
    </>
  );
}
